// Stub data for the home dashboard. Replace with a real /api/stats/home payload
// (single endpoint, see design doc) once the backend audit log lands.

export type GoalMetric =
  | 'WORDS_LEARNED'
  | 'WORDS_REVIEWED'
  | 'SESSIONS_DONE'
  | 'ACTIVE_PRACTICE_MINUTES';

export interface Goal {
  id: string;
  title: string;
  metric: GoalMetric;
  period: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  target: number;
  current: number;
  unit: string;
}

export interface WeeklyMetric {
  key: 'practiceMinutes' | 'wordsLearned' | 'wordsReviewed' | 'sessions';
  label: string;
  value: number;
  unit: string;
  deltaPct: number;
}

export interface Achievement {
  code: string;
  title: string;
  description: string;
  threshold: number;
  current: number;
  unlocked: boolean;
  unlockedAt?: string;
}

export interface Recommendation {
  id: string;
  title: string;
  hint: string;
  to: string;
  cta: string;
}

export interface HeatmapDay {
  date: string; // YYYY-MM-DD
  count: number; // 0..N — what colour intensity to render
  minutes: number;
}

export interface HomeStats {
  streak: {
    current: number;
    longest: number;
    activeToday: boolean;
  };
  todayGoal: Goal;
  weekly: WeeklyMetric[];
  goals: Goal[];
  nextAchievement: Achievement;
  recentAchievements: Achievement[];
  heatmap: HeatmapDay[];
  // GitHub-style: one full calendar year per entry; keyed by year (e.g. 2026).
  heatmapByYear: Record<number, HeatmapDay[]>;
  availableYears: number[];
  recommendations: Recommendation[];
  composition: {
    levels: { label: string; count: number; color: string }[];
  };
}

// ──────────────────────────────────────────────────────────────────────────────
// Generators. Deterministic so the UI doesn't jitter on every re-render.
// ──────────────────────────────────────────────────────────────────────────────

const DAY_MS = 24 * 60 * 60 * 1000;

// Format Date → "YYYY-MM-DD" using LOCAL components. `toISOString()` was buggy
// here: in UTC+2/+3 timezones it shifted Jan 1 2026 back to "2025-12-31",
// which then poisoned the month label of the heatmap.
const fmt = (d: Date) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const seededRand = (seed: number) => {
  // Mulberry32 — small deterministic PRNG. Lets us regenerate the same fake
  // heatmap on every reload without persisting it.
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const buildHeatmap = (weeks: number): HeatmapDay[] => {
  const rand = seededRand(42);
  const today = new Date();
  // Start from the Monday of the week `weeks - 1` ago so the grid is aligned.
  const start = new Date(today.getTime() - (weeks * 7 - 1) * DAY_MS);

  const days: HeatmapDay[] = [];
  for (let i = 0; i < weeks * 7; i += 1) {
    const date = new Date(start.getTime() + i * DAY_MS);
    // Make weekends quieter, recent week busier, and ~25% of days fully idle.
    const day = date.getDay();
    const recency = i / (weeks * 7);
    const weekendDrag = day === 0 || day === 6 ? 0.4 : 1;
    const idle = rand() < 0.25 ? 0 : 1;
    const raw = rand() * 4 * weekendDrag * (0.5 + recency) * idle;
    const count = Math.round(raw);
    days.push({
      date: fmt(date),
      count,
      minutes: count === 0 ? 0 : 5 + count * 8 + Math.floor(rand() * 10),
    });
  }
  return days;
};

// Full calendar year (Jan 1 → Dec 31) for a single year. Seeded by `year` so
// older years stay stable across reloads. For the current year, future days are
// emitted with count 0 so the heatmap doesn't "predict" activity.
const buildYearHeatmap = (year: number): HeatmapDay[] => {
  const rand = seededRand(year * 31 + 7);
  const today = new Date();
  const isCurrent = year === today.getFullYear();
  const days: HeatmapDay[] = [];

  const start = new Date(year, 0, 1);
  const end = new Date(year, 11, 31);
  const totalDays =
    Math.round((end.getTime() - start.getTime()) / DAY_MS) + 1;

  // Older years feel a bit lighter than recent ones — gives the picker a story.
  const yearWeight = year >= today.getFullYear() - 1 ? 1 : 0.65;

  for (let i = 0; i < totalDays; i += 1) {
    const date = new Date(start.getTime() + i * DAY_MS);
    const inFuture = isCurrent && date > today;
    if (inFuture) {
      days.push({ date: fmt(date), count: 0, minutes: 0 });
      continue;
    }
    const wd = date.getDay();
    const weekendDrag = wd === 0 || wd === 6 ? 0.45 : 1;
    const idle = rand() < 0.22 ? 0 : 1;
    const raw = rand() * 4 * weekendDrag * yearWeight * idle;
    const count = Math.round(raw);
    days.push({
      date: fmt(date),
      count,
      minutes: count === 0 ? 0 : 5 + count * 8 + Math.floor(rand() * 10),
    });
  }
  return days;
};

const HEATMAP_FULL = buildHeatmap(52);

const CURRENT_YEAR = new Date().getFullYear();
const AVAILABLE_YEARS = [
  CURRENT_YEAR,
  CURRENT_YEAR - 1,
  CURRENT_YEAR - 2,
];
const HEATMAP_BY_YEAR: Record<number, HeatmapDay[]> = AVAILABLE_YEARS.reduce(
  (acc, y) => {
    acc[y] = buildYearHeatmap(y);
    return acc;
  },
  {} as Record<number, HeatmapDay[]>,
);

export const MOCK_STATS: HomeStats = {
  streak: {
    current: 7,
    longest: 21,
    activeToday: true,
  },
  todayGoal: {
    id: 'today',
    title: 'Сьогоднішня ціль',
    metric: 'WORDS_REVIEWED',
    period: 'DAILY',
    target: 20,
    current: 13,
    unit: 'слів',
  },
  weekly: [
    {
      key: 'practiceMinutes',
      label: 'Час практики',
      value: 142,
      unit: 'хв',
      deltaPct: 12,
    },
    {
      key: 'wordsLearned',
      label: 'Вивчено нових',
      value: 38,
      unit: 'слів',
      deltaPct: 24,
    },
    {
      key: 'wordsReviewed',
      label: 'Повторено',
      value: 184,
      unit: 'слів',
      deltaPct: -8,
    },
    {
      key: 'sessions',
      label: 'Сесій',
      value: 11,
      unit: '',
      deltaPct: 5,
    },
  ],
  goals: [
    {
      id: 'g1',
      title: 'Вивчати по 10 слів щодня',
      metric: 'WORDS_LEARNED',
      period: 'DAILY',
      target: 10,
      current: 6,
      unit: 'слів',
    },
    {
      id: 'g2',
      title: '5 сесій вправ на тиждень',
      metric: 'SESSIONS_DONE',
      period: 'WEEKLY',
      target: 5,
      current: 3,
      unit: 'сесій',
    },
    {
      id: 'g3',
      title: '500 слів в активному словнику',
      metric: 'WORDS_LEARNED',
      period: 'MONTHLY',
      target: 500,
      current: 312,
      unit: 'слів',
    },
  ],
  nextAchievement: {
    code: 'WORDS_500',
    title: '500 вивчених слів',
    description: 'Доведи активний словник до 500 слів.',
    threshold: 500,
    current: 412,
    unlocked: false,
  },
  recentAchievements: [
    {
      code: 'STREAK_7',
      title: 'Тиждень поспіль',
      description: '7 днів активності без перерви.',
      threshold: 7,
      current: 7,
      unlocked: true,
      unlockedAt: '2026-06-09',
    },
    {
      code: 'WORDS_100',
      title: 'Сто слів',
      description: '100 слів у твоєму активному словнику.',
      threshold: 100,
      current: 100,
      unlocked: true,
      unlockedAt: '2026-05-22',
    },
    {
      code: 'FIRST_TEST',
      title: 'Перший тест',
      description: 'Пройдено перший тест словникового запасу.',
      threshold: 1,
      current: 1,
      unlocked: true,
      unlockedAt: '2026-05-12',
    },
  ],
  heatmap: HEATMAP_FULL,
  heatmapByYear: HEATMAP_BY_YEAR,
  availableYears: AVAILABLE_YEARS,
  recommendations: [
    {
      id: 'r1',
      title: '14 слів чекають на повторення',
      hint: 'Інтервальне повторення підказує, що сьогодні саме час.',
      to: '/exercises',
      cta: 'Повторити',
    },
    {
      id: 'r2',
      title: 'Не торкався «В дії» 12 днів',
      hint: 'Спробуй розібрати свіжий текст і знайти слова для вивчення.',
      to: '/in-action',
      cta: 'Відкрити',
    },
    {
      id: 'r3',
      title: 'Перевір словниковий запас',
      hint: 'Минув місяць від останнього тесту — час оновити оцінку.',
      to: '/test-my-vocabulary',
      cta: 'Пройти тест',
    },
  ],
  composition: {
    levels: [
      { label: 'A1', count: 120, color: '#27CCBC' },
      { label: 'A2', count: 184, color: '#BFB4FF' },
      { label: 'B1', count: 96, color: '#777BFF' },
      { label: 'B2', count: 42, color: '#3B2C93' },
      { label: 'C1+', count: 8, color: '#141414' },
    ],
  },
};
